import { IconButton } from '@creditornot/cb-button';
import { MoreHorizontal } from '@creditornot/cb-icons';
import { MenuActionItem } from '@creditornot/cb-menu';
import { LegacyPopover } from '@creditornot/cb-popover';
import { useMutation } from '@tanstack/react-query';
import React, { useCallback, useState } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { deleteOptionBinding } from 'apis/restaurant-api/merchant-admin/menu2/api';
import { ConfirmModal } from 'common/components';
import useMenuData from 'modules/menu/useMenuData';

type Props = {
  itemId: string;
  optionBindingId: string;
};

const Root = styled.div``;

const ToggleButton = styled(IconButton).attrs({ variant: 'brand', size: 'tiny' })``;

const PopoverContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 200px;
`;

const OptionBindingActions = ({ itemId, optionBindingId }: Props) => {
  const { updateMenuData, menu } = useMenuData();

  const [show, setShow] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  if (!menu) {
    throw Error('Menu should not be null');
  }

  const { mutate: handleRemoveOptionBinding, isLoading: submitting } = useMutation(
    () => deleteOptionBinding(menu.id, itemId, optionBindingId),
    {
      onSuccess: updatedItem => {
        updateMenuData({ actionType: 'updateItem', payload: updatedItem });
        toast.success(<FormattedMessage id="menu-option-instance.remove-from-item-success" />);
      },
      onError: () => {
        toast.error(<FormattedMessage id="errors.unknown-error-retry-contact" />);
      },
      onSettled: () => {
        setIsConfirmModalOpen(false);
      },
    },
  );

  const handleClose = useCallback(() => {
    setShow(false);
  }, []);

  return (
    <Root onClick={e => e.stopPropagation()}>
      <LegacyPopover
        show={show}
        onClickOutside={handleClose}
        onClickInside={handleClose}
        placement="bottom-start"
        renderArrow={false}
        content={
          <PopoverContent>
            <MenuActionItem onClick={() => setIsConfirmModalOpen(true)} variant="red">
              <FormattedMessage id="menu-option-instance.deleteOptionInstance" />
            </MenuActionItem>
          </PopoverContent>
        }
      >
        <ToggleButton onClick={() => setShow(v => !v)}>
          <MoreHorizontal />
        </ToggleButton>
      </LegacyPopover>
      <ConfirmModal
        header={<FormattedMessage id="menu-option-instance.deleteOptionInstance" />}
        onCancel={() => setIsConfirmModalOpen(false)}
        onConfirm={handleRemoveOptionBinding}
        open={isConfirmModalOpen}
        loading={submitting}
      />
    </Root>
  );
};

export default OptionBindingActions;
